import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import {
  changeProgramType,
  selectPartnerAccess,
} from 'legacy/lut/features/partner360/configuration'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations'
import { useSafetyTranslation } from 'cng-common/features/translations/hooks'
import { getUrlWithParams } from 'core-components/helpers/url'
import {
  ATTENTION_STATUS_ACTIONS,
  ATTENTION_STATUS_BITS,
  ATTENTION_STATUS_REMIND_DBM_ACTION_KEY,
} from 'partner360/entities/attentionStatuses'
import {
  getCompletenessItems,
  type ICompletenessItem,
  p360TabsIds,
} from 'partner360/entities/partnerCompleteness'
import {
  ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS,
  useAcknowledgmentPendingSigners,
} from 'partner360/features/acknowledgmentStatusDialog'
import { getTranslatedText } from 'partner360/helpers/getTranslatedText'
import { scrollToElement } from 'partner360/helpers/scrollToElement'
import { appendP360TabScrollIntent } from 'partner360/lib/tabScrollIntent'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { getAcknowledgedOfferingData } from '../../../../lib/getAcknowledgedOfferingData'
import {
  invalidateFinancialCockpitData,
  setCockpitOffering,
} from '../../../../model/financialCockpitSlice'
import { useNextIncompleteOffering } from '../../../../model/hooks/useNextIncompleteOffering'
import {
  selectCockpitCompletenessData,
  selectCockpitOffering,
  selectCockpitPartnerCompleteness,
  selectCockpitPartnerData,
  selectEffectiveCockpitFYear,
} from '../../../../model/selectors'
import { useCockpitSectionNavigation } from '../../../components/cockpitSectionNavigation'
import { useAttentionDetailExit } from '../../hooks/useAttentionDetailExit'
import { usePartnerPbmReminders } from '../../hooks/usePartnerPbmReminders'
import type { IAttentionStatusesItem } from '../../types'

import { AttentionNextStepRow } from './AttentionNextStepRow'
import { AttentionNextStepsGroup } from './AttentionNextStepsGroup'
import { NEXT_STEPS_LIMIT } from './constants'
import { getStepInitials } from './getStepInitials'
import { usePlanSignFlow } from './usePlanSignFlow'

import styles from './AttentionNextSteps.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.nextSteps
const REMINDER_KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.reminder
const ACK_KEYS = ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS

/* Truncated rather than rounded, as the completeness detail reads it: 99.8% is not a finished
   section. */
const toPercent = (value: number | undefined) => Math.floor((value ?? 0) * 100)

/* From the cockpit the Basic info and Contacts widgets both live on the Basic info tab, so their
   steps open that tab rather than scrolling the page — as the completeness detail opens them. */
const COCKPIT_BASIC_INFO_URL_PARAMS = { 'jbp-tab': '23' }
const COCKPIT_BASIC_INFO_TAB_ITEMS = new Set(['basic', 'contact'])

const toCockpitCompletenessItem = (item: ICompletenessItem): ICompletenessItem =>
  COCKPIT_BASIC_INFO_TAB_ITEMS.has(item.id)
    ? { ...item, scrollElementId: undefined, urlParams: COCKPIT_BASIC_INFO_URL_PARAMS }
    : item

interface IPlannedNextStepsProps {
  item: IAttentionStatusesItem
  onSelect: (id: string) => void
}

/**
 * `Offering X is not planned`: the plan's first sections still short of 100%, in the order the
 * completeness bars list them, each opening its section. A plan measured full is held only by its
 * signatures, so the steps left are its signers — the required ones in signing order, then the
 * reader's own optional row.
 */
export const PlannedNextSteps = ({ item, onSelect }: IPlannedNextStepsProps) => {
  const t = useSafetyTranslation()
  const dispatch = useAppDispatch()
  const cockpitOffering = useSelector(selectCockpitOffering)
  const { isRemote, openSection, openUrl } = useCockpitSectionNavigation()
  const exitDetail = useAttentionDetailExit()
  const completenessProfiles = useSelector(selectCockpitPartnerCompleteness)
  const completenessData = useSelector(selectCockpitCompletenessData)
  const partnerData = useSelector(selectCockpitPartnerData)
  const fYear = useSelector(selectEffectiveCockpitFYear)
  const { isDistributor } = useSelector(selectPartnerAccess)

  const offering = item.offering ?? null
  const partnerId = partnerData[0]?.PartnerId ?? ''

  const openItems = useMemo(() => {
    const completeness = completenessProfiles.find(({ profile }) => profile === offering)

    return getCompletenessItems(completeness, isDistributor)
      .map(toCockpitCompletenessItem)
      .filter(({ data }) => toPercent(data) < 100)
  }, [completenessProfiles, isDistributor, offering])

  const isSigned = useMemo(
    () => Boolean(offering && getAcknowledgedOfferingData(completenessData, partnerId, offering)),
    [completenessData, offering, partnerId]
  )

  /* Who signs is read from the plan's own signers, and only once the signature is all that is
     left. */
  const waitsForSignature = openItems.length === 0 && !isSigned
  const handleSignersUpdated = useCallback(() => {
    dispatch(invalidateFinancialCockpitData())
  }, [dispatch])
  const {
    hasLoaded: hasSignersLoaded,
    isOwnRow,
    planSigners,
    isOptionalSigner,
    isReminded: isSignerReminded,
    remind: remindSigner,
    isSubmitting: isRemindingSigner,
  } = useAcknowledgmentPendingSigners({
    partnerId,
    fYear,
    offering: waitsForSignature ? offering : null,
    onUpdated: handleSignersUpdated,
  })

  /* Every row here is one the server marks `SignoffPending`, so the reader signs their own row;
     every other signature the plan lacks is reminded, as it waits for that signer whether or not
     it is their turn yet. A reminder the server holds (`-1`) until another plan is complete says
     why and leads to that plan. */
  const getRowAct = (signer: (typeof planSigners)[number]) => {
    if (isOwnRow(signer)) {
      return 'sign'
    }

    return signer.CanResend < 0 ? 'blocked' : 'remind'
  }

  const nextOffering = useNextIncompleteOffering(offering)
  const nextOfferingParams = nextOffering
    ? [{ key: '{Offering}', value: getTranslatedText({ t, key: nextOffering.titleKey }) }]
    : undefined

  const signFlow = usePlanSignFlow({ partnerId, fYear, offering })
  const pbmReminders = usePartnerPbmReminders(partnerId, offering, ATTENTION_STATUS_BITS.planned)

  const handleOpenSection = useCallback(
    (section: ICompletenessItem) => {
      /* `Overall` has no sections of its own, so the step first moves the cockpit to the
         offering the status was raised for, as a related status opens its own tab. */
      if (cockpitOffering === ProgramTypes.Overall && offering) {
        dispatch(changeProgramType(offering as ProgramTypes))
        dispatch(setCockpitOffering(offering as ProgramTypes))
      }

      /* A section with a tab of its own opens in its window over the cockpit; the others are on
         the page, so the page scrolls to them and the expanded tile gets out of the way. */
      if (section.urlParams && !isRemote) {
        openUrl(appendP360TabScrollIntent(getUrlWithParams(section.urlParams)))
        return
      }

      scrollToElement(section.scrollElementId ?? p360TabsIds)

      if (section.urlParams) {
        openSection(section.urlParams)
        return
      }

      exitDetail()
    },
    [cockpitOffering, dispatch, exitDetail, isRemote, offering, openSection, openUrl]
  )

  if (openItems.length === 0) {
    /* Nothing is said until the signers are read, so the line does not first offer the wrong
       button. Every signature the plan still lacks is a step of its own: the required ones in
       signing order, then the reader's own optional row. The reader's own row signs and another
       signer's row reminds them. The reader's own row is never pushed out by the limit. Only a plan with no signer to name
       falls back to reminding the PBM. */
    const ownIndex = planSigners.findIndex(isOwnRow)
    const othersLimit = ownIndex >= NEXT_STEPS_LIMIT ? NEXT_STEPS_LIMIT - 1 : NEXT_STEPS_LIMIT
    const shownSigners = planSigners.filter((_, index) => index < othersLimit || index === ownIndex)
    const remindsPbm = planSigners.length === 0
    const signStep = !waitsForSignature ? null : !hasSignersLoaded ? undefined : (
      <>
        {remindsPbm && (
          <AttentionNextStepRow
            mark={{ kind: 'plan', text: '' }}
            name={<Translation span text={KEYS.signPlan} />}
            fact={<Translation span text={KEYS.allSectionsComplete} />}
            tone="remind"
            severity={item.severity}
            labelKey={
              /* The tooltip says why a reminder cannot go out, rather than offering it. */
              pbmReminders.recipients.length === 0
                ? REMINDER_KEYS.blockedNoRecipient
                : pbmReminders.isReminded
                  ? KEYS.remindedToday
                  : isDistributor
                    ? ATTENTION_STATUS_REMIND_DBM_ACTION_KEY
                    : ATTENTION_STATUS_ACTIONS.remindPbm.labelKey
            }
            onClick={pbmReminders.remindPbm}
            loading={pbmReminders.isPending}
            disabled={pbmReminders.recipients.length === 0 || pbmReminders.isReminded}
          />
        )}
        {shownSigners.map((signer) => {
          const isOwn = isOwnRow(signer)
          const name = signer.SignerName?.trim() || signer.SignerEmail || ''
          const act = getRowAct(signer)
          const reminded = act === 'remind' && isSignerReminded(signer)

          if (act === 'blocked') {
            return (
              <AttentionNextStepRow
                key={signer.EntryId}
                mark={{ kind: 'person', text: getStepInitials(name) }}
                name={
                  <Translation
                    span
                    text={KEYS.pendingAcknowledgment}
                    replaceParams={[
                      { key: '{SignerName}', value: name },
                      { key: '{SignerPost}', value: '' },
                    ]}
                  />
                }
                fact={signer.AlertKey ? <Translation span text={signer.AlertKey} /> : undefined}
                stacked
                tone={nextOffering ? 'go' : 'remind'}
                severity={item.severity}
                labelKey={nextOffering ? KEYS.openOffering : KEYS.remind}
                labelParams={nextOfferingParams}
                disabled={!nextOffering}
                onClick={() => nextOffering?.open()}
              />
            )
          }

          return (
            <AttentionNextStepRow
              key={signer.EntryId}
              mark={{ kind: 'person', text: getStepInitials(name), isOwn }}
              name={
                <Translation
                  span
                  text={KEYS.pendingAcknowledgment}
                  replaceParams={[
                    { key: '{SignerName}', value: name },
                    {
                      key: '{SignerPost}',
                      value: [signer.CommitmentTypeName, signer.SignerRole]
                        .filter(Boolean)
                        .join(' · '),
                    },
                  ]}
                />
              }
              badge={
                isOwn ? (
                  <span className={styles.you}>
                    <Translation
                      span
                      text={ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.common.you}
                    />
                  </span>
                ) : undefined
              }
              fact={
                isOptionalSigner(signer) ? (
                  <Translation span text={ACK_KEYS.common.optional} />
                ) : undefined
              }
              tone={act}
              severity={item.severity}
              labelKey={
                act === 'sign'
                  ? ATTENTION_STATUS_ACTIONS.acknowledge.labelKey
                  : reminded
                    ? KEYS.remindedToday
                    : KEYS.remind
              }
              loading={act === 'sign' ? signFlow.isSigning : false}
              disabled={act === 'remind' && (reminded || isRemindingSigner)}
              onClick={
                act === 'sign'
                  ? () => signFlow.start(signer.EntryId)
                  : () => void remindSigner(signer)
              }
            />
          )
        })}
        {signFlow.element}
      </>
    )

    return (
      <AttentionNextStepsGroup
        item={item}
        onSelect={onSelect}
        restCount={
          waitsForSignature && hasSignersLoaded ? planSigners.length - shownSigners.length : 0
        }
      >
        {signStep}
      </AttentionNextStepsGroup>
    )
  }

  const shown = openItems.slice(0, NEXT_STEPS_LIMIT)

  return (
    <AttentionNextStepsGroup
      item={item}
      onSelect={onSelect}
      restCount={openItems.length - shown.length}
    >
      {shown.map((section) => (
        <AttentionNextStepRow
          key={section.id}
          mark={{
            kind: 'section',
            text: getStepInitials(getTranslatedText({ t, key: section.label })),
            color: section.color,
          }}
          name={<Translation span text={section.label} />}
          fact={
            <Translation
              span
              text={KEYS.sectionPercent}
              replaceParams={[{ key: '{percent}', value: String(toPercent(section.data)) }]}
            />
          }
          tone="go"
          severity={item.severity}
          labelKey={KEYS.openSection}
          onClick={() => handleOpenSection(section)}
        />
      ))}
    </AttentionNextStepsGroup>
  )
}

PlannedNextSteps.displayName = 'PlannedNextSteps'
